import { WifiStrong, WifiOk, WifiWeak, WifiNoSignal } from "svgs";
import styled from "styled-components/macro";
import { useInterval } from "react-use";
import { useState } from "react";
import { BREAKPOINT_MOBILE } from "constants/constants";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

const WIDTH = 24;
const PADDING = 12;

const WifiBarsStyles = styled.div`
  font-size: 1.5rem;
  position: absolute;
  right: ${WIDTH + 2 * PADDING + 8}px;
  /* if we support overflow: overlay, add scrollbar width to padding-right so we don't fall behind the scrollbar */
  @supports (overflow: overlay) {
    /* disable for mobile where scrollbar is invisible */
    @media (min-width: ${BREAKPOINT_MOBILE}px) {
      right: ${WIDTH + 2 * PADDING + 8 + 16}px;
    }
  }
  width: ${WIDTH}px;
  height: ${WIDTH}px;
  padding: ${PADDING}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const IMAGE_ADDR = "/images/Example.jpg";
const DOWNLOAD_SIZE_BYTES = 27660;

function WifiBars() {
  const [
    {
      general: { wifiBarsUpdateIntervalMs },
    },
  ] = useFlightConfig();
  return wifiBarsUpdateIntervalMs === 0 ? null : <WifiBarsContent />;
}

/** Wifi icon that repeatedly checks internet connection level
 * * in 2023, the browser Network Information API (react-use > useNetworkState() hook) is not supported in most browsers
 * * so instead we download a small image every X seconds to check connection speed...
 */
const WifiBarsContent = () => {
  const [
    {
      general: { wifiBarsUpdateIntervalMs },
    },
  ] = useFlightConfig();
  const speedBps = useDetectConnectionSpeedOnInterval(
    wifiBarsUpdateIntervalMs * (process.env.NODE_ENV === "development" ? 10 : 1)
  );
  const speedMbitps = getSpeedMbitps(speedBps);
  return wifiBarsUpdateIntervalMs === 0 ? null : (
    <WifiBarsStyles
      className="wifiBars doNotPrint"
      data-component-name={"Wifi Bars"}
    >
      {/*
      based on levels provided by network tab throttle options:
     (these are numbers I found using desktop & laptop PCs)
     Online -> 2.9 on ethernet
     Fast 3G -> 0.29 on ethernet
     Slow 3G -> 0.082 on ethernet
     */}
      {speedMbitps === undefined ? null : speedMbitps > 0.3 ? (
        <WifiStrong />
      ) : speedMbitps > 0.2 ? (
        <WifiOk />
      ) : speedMbitps > 0 ? (
        <WifiWeak />
      ) : (
        <WifiNoSignal />
      )}
    </WifiBarsStyles>
  );
};

export default WifiBars;

/** detect internet speed by downloading a specific image of known size https://gist.github.com/ionurboz/b0c5ab85bc0280eaffe418b9b5c9336a */
function useDetectConnectionSpeedOnInterval(interval: number): number {
  const [speedBps, setSpeedBps] = useState<number>(0);

  useInterval(() => {
    if (interval === 0) {
      return;
    }
    function measureConnectionSpeed() {
      let endTime;
      const download = new Image();
      download.onload = function () {
        endTime = new Date().getTime();
        showResults();
      };

      download.onerror = function (err, msg) {
        setSpeedBps(0);
        console.log("Invalid image, or error downloading");
      };

      const startTime = new Date().getTime();
      const cacheBuster = "?nnn=" + startTime;
      download.src = IMAGE_ADDR + cacheBuster;

      function showResults() {
        const duration = (endTime - startTime) / 1000;
        const bitsLoaded = DOWNLOAD_SIZE_BYTES * 8;
        const speedBps = bitsLoaded / duration;
        setSpeedBps(speedBps);
        const speedMbitps = getSpeedMbitps(speedBps)?.toFixed(2);
        console.log("Connection speed: ", speedMbitps, "Mbit/s");
      }
    }

    measureConnectionSpeed();
  }, interval);
  return speedBps;
}

function getSpeedMbitps(speedBps: number): number {
  return speedBps / 1000000;
}
